import { Box, Stack } from "@mui/material";
import moment from "moment-timezone";
import { extendMoment } from "moment-range";
import { IAvailabilityDocData } from "../../services/api/types/roostermeeting.type";
import { useI18nLanguage } from "../../hooks/i18nLanguage";

const momentRange = extendMoment(moment);

interface ISubmittedAvailabilitiesProps {
  submittedAvailabilities: IAvailabilityDocData["availability"];
  availabilityTimezone: IAvailabilityDocData["clientTimeZone"];
}

/**
 * Component that renders a list of submitted availabilities in a scrollable container.
 *
 * The component handles both all-day availabilities and specific time slots,
 * displaying them in a user-friendly format according to the provided timezone.
 * All-day availabilities are rendered with "(All Day)" label for each day.
 * Time-specific availabilities display both start and end time, with date formats
 * adjusted based on whether start and end are on the same day.
 *
 * The container includes a custom scrollbar that appears only on hover for better UX.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Array<{start: string, end: string}>} props.submittedAvailabilities - Array of availability objects with start and end timestamps
 * @param {string} props.availabilityTimezone - Timezone to use when displaying the availability times
 *
 * @example
 * <SubmittedAvailabilities
 *   submittedAvailabilities={[
 *     { start: '2023-06-01T09:00:00Z', end: '2023-06-01T10:00:00Z' }
 *   ]}
 *   availabilityTimezone="America/New_York"
 * />
 */
export const SubmittedAvailabilities: React.FC<
  ISubmittedAvailabilitiesProps
> = ({ submittedAvailabilities, availabilityTimezone }) => {
  const { selectedLanguageForMoment } = useI18nLanguage();
  return (
    <>
      <Stack
        spacing={1}
        sx={{
          mb: "20px",
          maxHeight: "300px",
          overflow: "auto",
          width: "100%",
          "&::-webkit-scrollbar": {
            width: "6px",
            display: "none" // Initially hide scrollbar
          },
          "&:hover::-webkit-scrollbar": {
            display: "block" // Show scrollbar on hover
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent"
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.3)"
                : "rgba(0,0,0,0.3)",
            borderRadius: "3px"
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.5)"
                : "rgba(0,0,0,0.5)"
          }
        }}
      >
        {submittedAvailabilities.map((availability) => {
          const startMoment = moment(availability.start).tz(
            availabilityTimezone
          );
          const endMoment = moment(availability.end).tz(availabilityTimezone);

          // Check if it's an all-day availability: either starts at midnight and ends at midnight,
          // or if it spans exactly 24 hours
          const isAllDay =
            availability?.allDay &&
            startMoment.isSame(endMoment.subtract(1, "minute"), "day");

          return (
            <Box
              key={availability.start}
              sx={{
                fontWeight: 450
              }}
            >
              {isAllDay ? ( // all day availability
                <Box
                  sx={{
                    fontWeight: 450
                  }}
                >
                  {Array.from(
                    momentRange
                      .range(
                        moment(availability.start),
                        moment(availability.end).subtract(1, "ms")
                      )
                      .by("days")
                  ).map((date) => (
                    <div key={date.format()}>
                      {moment(date)
                        .tz(availabilityTimezone)
                        .locale(selectedLanguageForMoment)
                        .format("ddd, DD-MMM-YYYY")}{" "}
                      <i>(All Day)</i>
                    </div>
                  ))}
                </Box> // non-all day availability
              ) : (
                <Box
                  sx={{
                    fontWeight: 450
                  }}
                >
                  {moment(availability.start)
                    .tz(availabilityTimezone)
                    .locale(selectedLanguageForMoment)
                    .format("ddd, DD-MMM-YYYY")}
                  ,{" "}
                  {moment(availability.start)
                    .tz(availabilityTimezone)
                    .locale(selectedLanguageForMoment)
                    .format("LT")}{" "}
                  -{" "}
                  {moment(availability.start)
                    .tz(availabilityTimezone)
                    .isSame(
                      moment(availability.end).tz(availabilityTimezone),
                      "day"
                    ) // second part of the record (checks if start and end are on the same day)
                    ? moment(availability.end) // if same day, only show time
                        .tz(availabilityTimezone)
                        .locale(selectedLanguageForMoment)
                        .format("LT")
                    : moment(availability.end) // check if end is on the next day
                          .tz(availabilityTimezone)
                          .isSame(
                            moment(availability.start)
                              .tz(availabilityTimezone)
                              .add(1, "day"),
                            "day"
                          )
                      ? `${moment(availability.end)
                          .tz(availabilityTimezone)
                          .locale(selectedLanguageForMoment)
                          .format("ddd, DD-MMM-YYYY")}, ${moment(
                          availability.end
                        )
                          .tz(availabilityTimezone)
                          .locale(selectedLanguageForMoment)
                          .format("LT")}`
                      : `${moment(availability.end)
                          .tz(availabilityTimezone)
                          .locale(selectedLanguageForMoment)
                          .format("ddd, DD-MMM-YYYY")}, ${moment(
                          availability.end
                        )
                          .tz(availabilityTimezone)
                          .locale(selectedLanguageForMoment)
                          .format("LT")}`}{" "}
                </Box>
              )}
            </Box>
          );
        })}
      </Stack>
    </>
  );
};
